import { RefreshCw } from 'lucide-react'
import type { AutoRefresh } from './useAutoRefresh'

// D-VIEWS (Maya round table, 2026-08-06): the ONE refresh control shown identically on every view —
// an auto-refresh toggle, an "updated Xs ago" indicator, and a manual refresh button. Wired to the
// shared useAutoRefresh hook so system views, custom views, and any new view look and behave the same.

function ago(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000))
  if (s < 5) return 'just now'
  if (s < 60) return `${s}s ago`
  const m = Math.round(s / 60)
  return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`
}

export function RefreshControl({ ar, intervalSec = 15 }: { ar: AutoRefresh; intervalSec?: number }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
      <button
        type="button"
        onClick={() => ar.setAutoOn(!ar.autoOn)}
        title={ar.autoOn ? `Auto-refreshing every ${intervalSec}s — click to pause` : 'Auto-refresh paused — click to resume'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer',
          border: `1px solid ${ar.autoOn ? '#6ee7b7' : '#cbd5e1'}`,
          background: ar.autoOn ? '#ecfdf5' : '#f8fafc',
          color: ar.autoOn ? '#065f46' : '#64748b',
          borderRadius: 20, padding: '5px 11px', fontSize: 12, fontWeight: 700,
        }}
      >
        <span style={{
          width: 30, height: 17, borderRadius: 20, position: 'relative', flex: '0 0 auto',
          background: ar.autoOn ? '#059669' : '#cbd5e1', transition: 'background .15s',
        }}>
          <span style={{
            position: 'absolute', top: 2, [ar.autoOn ? 'right' : 'left']: 2,
            width: 13, height: 13, borderRadius: '50%', background: '#fff',
          } as React.CSSProperties} />
        </span>
        Auto-refresh · {intervalSec}s
      </button>
      <span style={{ fontSize: 12, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: ar.refreshing ? '#f59e0b' : '#059669' }} />
        {ar.refreshing ? 'Updating…' : `Updated ${ago(ar.lastUpdated)}`}
      </span>
      <button
        type="button"
        onClick={ar.refreshNow}
        disabled={ar.refreshing}
        title={`Auto-refreshes every ${intervalSec}s · click to refresh now`}
        style={{
          width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#334155',
          cursor: ar.refreshing ? 'default' : 'pointer',
        }}
      >
        <RefreshCw size={16} style={ar.refreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
      </button>
    </div>
  )
}
